import { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "@/components/base/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import { categories, getMoviesByCategory } from "@/mocks/movies";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const [activeSlug, setActiveSlug] = useState(slug || "all");
  const [sortBy, setSortBy] = useState<"rating" | "date" | "views">("rating");

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);
  const movies = useMemo(() => {
    const list = getMoviesByCategory(activeSlug);
    return [...list].sort((a, b) => {
      if (sortBy === "rating") return b.rating - a.rating;
      if (sortBy === "date") return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
      return b.viewCount - a.viewCount;
    });
  }, [activeSlug, sortBy]);

  const categoryJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${activeCategory?.name || "全部"} - 酷客影院`,
    description: activeCategory?.description || "浏览全部影视内容",
    url: `https://www.kukecinema.com/category/${activeSlug}`,
    isPartOf: {
      "@type": "WebSite",
      name: "酷客影院",
      url: "https://www.kukecinema.com",
    },
  };

  return (
    <>
      <Helmet>
        <title>{activeCategory?.name || "全部影视"} - 酷客影院 | 高清在线观看</title>
        <meta name="description" content={`酷客影院${activeCategory?.name || "全部影视"}分类，提供海量${activeCategory?.description || "高清影视内容"}，支持超清播放，画质清晰流畅。`} />
        <meta name="keywords" content={`酷客影院,${activeCategory?.name || "全部影视"},高清电影,在线观看,超清播放`} />
        <link rel="canonical" href={`https://www.kukecinema.com/category/${activeSlug}`} />
        <script type="application/ld+json">{JSON.stringify(categoryJsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="px-4 md:px-6 py-6">
            <div className="max-w-screen-2xl mx-auto">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: activeCategory?.name || "全部影视" },
                ]}
              />
            </div>
          </div>

          <section className="px-4 md:px-6 pb-4">
            <div className="max-w-screen-2xl mx-auto">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                {activeCategory?.name || "全部影视"}
                <span className="text-foreground-400 text-base font-normal ml-3">
                  ({movies.length}部)
                </span>
              </h1>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={`/category/${cat.slug}`}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                        activeSlug === cat.slug
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-600 hover:bg-background-200"
                      }`}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
                <div className="hidden sm:block flex-1"></div>
                <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
                  {[
                    { key: "rating", label: "评分最高" },
                    { key: "date", label: "最新上线" },
                    { key: "views", label: "播放最多" },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key as typeof sortBy)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                        sortBy === opt.key
                          ? "bg-background-50 text-foreground-950 shadow-sm"
                          : "text-foreground-500 hover:text-foreground-800"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-6">
            <div className="max-w-screen-2xl mx-auto">
              {movies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                  {movies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 mx-auto mb-4">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">暂无相关内容</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}